import {
    Box,
    Card,
    Typography,
    Alert,
    Grid,
    MenuItem,
    TextField,
} from '@mui/material';
import { useEffect, useState, lazy, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux-store/appStore';
// import LoadingOverlay from 'src/components/common/LoadingOverlay';

// Import your permit-specific components or use lazy loading
const ConstructionPermitComponent = lazy(() => import('./ConstructionPermitConfiguration'));

const PermitConfiguration: React.FC = () => {
    const userDetails = useSelector((store: RootState) => store.user.userDetails);
    const [permitOptions, setPermitOptions] = useState([]);
    const [selectedPermit, setSelectedPermit] = useState<string | number>('');
    // const [isLoading, setIsLoading] = useState<boolean>(false);

    // Extract permit names for the finance manager role
    useEffect(() => {
        if (userDetails && userDetails.TownshipPermits) {
            let permits = [];
            if (userDetails.UserRole === 'Admin') {
                // Admin: Show all permits
                permits = userDetails.TownshipPermits.flatMap((department) =>
                    department.Permits.map((permit) => ({
                        PermitID: permit.PermitID,
                        PermitName: permit.PermitName
                    }))
                );
            } else {
                // Finance Manager: Filter permits by UserRoleName
                permits = userDetails.TownshipPermits.flatMap((department) =>
                    department.Permits.filter(
                        (permit) => permit.UserRoleName === 'Finance Manager'
                    )
                ).map((permit) => ({
                    PermitID: permit.PermitID,
                    PermitName: permit.PermitName
                }));
            }
            setPermitOptions(permits);
            if (permits.length > 0) {
                setSelectedPermit(permits[0].PermitID); // Set the first permit as the default value
            }
        }
    }, [userDetails]);

    // Component mapping object
    const permitComponentMap = {
        // Map permit IDs to their respective components
        9: ConstructionPermitComponent
        // Add more mappings as you create components
    };

    // Function to render the appropriate component based on selected permit
    const renderPermitComponent = () => {
        if (!selectedPermit) {
            return (
                <Box mt={3}>
                    <Alert severity="info">Please select a permit to configure.</Alert>
                </Box>
            );
        }

        const PermitComponent = permitComponentMap[selectedPermit];

        if (!PermitComponent) {
            return (
                <Box mt={3}>
                    <Alert severity="info">
                        Configuration for this permit type is not available yet.
                        Please check back later or contact the administrator.
                    </Alert>
                </Box>
            );
        }

        return (
            <Suspense fallback={<Box mt={3}>Loading permit configuration...</Box>}>
                <PermitComponent permitId={selectedPermit} />
            </Suspense>
        );
    };

    return (
        <>
            {/* {isLoading && <LoadingOverlay />} */}
            <Card>
                <Box m={3}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        You are about to update the application fee, credit card charges,
                        and inspection fee settings. Kindly verify that all values are
                        correct before proceeding, as these changes will directly impact
                        financial transactions.
                    </Alert>
                    <Typography variant="h3" mb={3} color={'#6E759F'}>
                        Permit finance configuration
                    </Typography>

                    {/* Permit Type Dropdown */}
                    <Grid item xs={12} sm={6} mb={3}>
                        <TextField
                            select
                            label="Permit Type"
                            name="permit-type-select"
                            value={selectedPermit}
                            onChange={(e) => setSelectedPermit(e.target.value)}
                            fullWidth
                        >
                            {permitOptions.map((permit) => (
                                <MenuItem key={permit.PermitID} value={permit.PermitID}>
                                    {permit.PermitName}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    {/* Render the appropriate component based on selected permit */}
                    {renderPermitComponent()}
                </Box>
            </Card>
        </>
    );
};

export default PermitConfiguration;